import { CopyToClipboardButton } from "components/CopyToClipboard/CopyToClipboard.styles";
import Icon, { IconPng } from "components/Icon/Icon";
import Link from "components/Link/Link";
import PreLoader from "components/PreLoader/PreLoader";
import { TooltipTrigger } from "components/Tooltip/Tooltip";
import styled from "styled-components";
import { ParcelCol, ParcelRow } from "styles/parcelsStyles";
import { CustomRasterIcon } from "containers/Storage/components/ServiceRequests/ServiceRequest.styles";

export const Wrapper = styled.div`
  width: 100%;
  padding-bottom: 20px;
`;

export const Spinner = styled(PreLoader)`
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 0 auto;
`;

interface StorageItemRowWrapperProps {
  disabled: boolean;
}

export const StorageItemRowWrapper = styled.div<StorageItemRowWrapperProps>`
  &:not(:first-child) > div {
    border-top: none;
  }

  ${({ disabled }) =>
    disabled &&
    `
      opacity: 0.5;
      pointer-events: none;
    `}
`;

export const StorageCol = styled(ParcelCol)`
  text-align: left;
  line-height: 20px;
  white-space: nowrap;
  user-select: none;

  .tooltip-content-checkbox {
    min-width: 18px;
  }

  &:nth-child(4) > div {
    margin-left: 15px;
  }

  ${(props) => props.theme.lg`
    white-space: normal;
  `}

  ${(props) => props.theme.md`
    white-space: nowrap;
  `}

  ${(props) => props.theme.xs`
    &:nth-child(4) > div {
      margin-left: 10px;
    }
  `}
  
  ${(props) => props.theme.xs`
    margin-bottom: 5px;
  `}
`;

export const MainStorageCol = styled(StorageCol)`
  padding-left: 0 !important;
`;

/*
 * Desktop row cells. The trailing weight/date cells are content-sized so the
 * spare width collects in the gap after the tracking number instead of being
 * spread across percentage columns; the description cell absorbs the rest.
 */
export const StorageDescriptionCol = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 0%;
  min-width: 0;
  padding: 0 10px;
  text-align: left;
  line-height: 20px;
  user-select: none;

  .tooltip-content-checkbox {
    min-width: 18px;
  }
`;

export const StorageMetaCol = styled.div<{ $width?: number }>`
  display: flex;
  align-items: center;
  flex: 0 0 ${({ $width }) => ($width ? `${$width}px` : "auto")};
  ${({ $width }) => $width && `width: ${$width}px;`}
  padding: 0 10px;
  text-align: left;
  line-height: 20px;
  white-space: nowrap;
  user-select: none;
`;

interface StorageRowProps {
  styled: {
    hasConsolidation: boolean;
  };
}

export const StorageRow = styled(ParcelRow)<StorageRowProps>`
  ${({ theme }) => theme.range("padding", "0px", "6px")};

  ${(props) => props.theme.xs`
    padding: 5px 0 0;
  `};

  & > ${StorageCol} {
    &:first-child {
      justify-content: space-between;
    }

    &:last-child {
      flex-wrap: wrap;
      margin-bottom: 0;

      ${({ theme, styled }) =>
        styled.hasConsolidation &&
        theme.xs`
          & > div:nth-child(3) > div {
            margin-left: 0px;
          }
        `}
    }
  }
`;

export const NoCheckboxActionArea = styled.div`
  align-items: center;
  display: flex;
  padding: 15px 10px 15px 15px;
  margin-right: 15px;
`;

export const CheckboxAreaButton = styled.button`
  align-items: center;
  cursor: pointer;
  display: flex;
  padding: 15px 10px 15px 15px;
  margin-right: 15px;
`;

export const EmptyConsolidationSpacing = styled.div`
  width: 18px;
`;

export const MobileCheckboxAreaButton = styled.button`
  align-items: center;
  justify-content: center;
  cursor: pointer;
  display: flex;
  min-height: 60px;
  padding: 0 15px;
  margin-right: 8px;
`;

export const MobileCheckboxAreaButton2 = styled(MobileCheckboxAreaButton)`
  padding: 0 31px;
`;

interface DescriptionProps {
  disabled?: boolean;
}

export const Description = styled.div<DescriptionProps>`
  color: ${({ theme }) => theme.accentText};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  line-height: 17px;
  min-width: 0;
  max-width: 100%;
  img {
    margin: 0;
  }

  /* Let the tracking number shrink to its middle-ellipsis form instead of
     wrapping or overflowing the column. */
  ${TooltipTrigger} {
    min-width: 0;
    max-width: 100%;
  }

  ${CopyToClipboardButton} {
    display: flex;
    min-width: 0;
    max-width: 100%;
  }

  ${(props) => props.theme.sm`
    margin-left: 12px;
  `}

  ${(props) => props.theme.xs`
    margin-left: 0;

    img {
      width: 12px;
      height: 12px;
    }
  `}

  ${({ disabled, theme }) => disabled && `color: ${theme.black50};`}
`;

export const IconGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;

  ${(props) => props.theme.xs`
    ${CustomRasterIcon} {
      width: 12px;
      height: 12px;
    }

    ${TooltipTrigger} {
      width: 12px;
      height: 12px;
    }
  `}
`;

export const TrackingNumber = styled.span`
  display: flex;
  min-width: 0;
  max-width: 100%;
  white-space: nowrap;
`;

export const TrackingNumberStart = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const TrackingNumberEnd = styled.span`
  flex-shrink: 0;
`;

export const Comment = styled.span`
  font-size: 12px;
  text-align: left;
  width: 100%;
  color: ${(props) => props.theme.black50};
`;

export const DemoBadge = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  line-height: 16px;
  padding: 0 5px;
  border: 1px solid transparent;
  border-radius: 2px;
  background: ${(props) => props.theme.primary10};
  color: ${(props) => props.theme.primaryAccent};
`;

export const WeightText = styled.span`
  && {
    color: ${({ theme }) => theme.rowMetaText};
  }
`;

export const ParcelDate = styled.div`
  color: ${({ theme }) => theme.rowMetaText};
`;

export const RedirectLink = styled(Link)`
  display: flex;
  align-items: center;
  padding: 10px 0;

  span {
    font-weight: 700;
    margin-left: 4px;
  }

  &:hover i {
    opacity: 0.5;
  }

  ${(props) => props.theme.md`
    margin-left: auto;
  `}

  ${(props) => props.theme.sm`
    span:first-child {
      display: none;
    }
  `}

  ${(props) => props.theme.xs`
    padding: 0;

    span {
      margin-left: 0;
    }
  `}
`;

export const RedirectLinksGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0 4px;
  padding: 10px 0;
  color: ${(props) => props.theme.darkGrey};

  ${(props) => props.theme.md`
    margin-left: auto;
    justify-content: flex-end;
  `}

  ${(props) => props.theme.xs`
    padding: 0;
  `}
`;

export const RedirectLabel = styled.span`
  font-weight: 700;

  ${(props) => props.theme.sm`
    display: none;
  `}
`;

export const RedirectTokenLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-weight: 700;

  &:hover i {
    opacity: 0.5;
  }
`;

export const RightArrowIcon = styled(Icon)`
  width: 8px;
  height: 14px;
  margin-left: 6px;
  transition: opacity 0.2s;
`;

export const WarningText = styled.div`
  width: 18px;
  height: 22px;
  color: ${({ theme }) => theme.red};
  font-size: 20px;
  text-align: center;
`;

export const TransferIcon = styled(IconPng)`
  position: absolute;
  top: 18%;
  right: 0;
`;
